import _ from "lodash";
import { useEffect, useState, memo, ReactNode, MouseEvent } from "react";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Grid,
  Collapse,
} from "@mui/material";
import { amber, blueGrey } from "@mui/material/colors";

import CurrentSession from "data/userSession";
import User from "data/firestore/user";
import useCustomNavigation from "hooks/customNavigation";
import { useOpenProfile } from "features/progress/Overlay";
import useCustomSnackbar from "hooks/customSnackbar";
import Logger from "utils/logger";
import AnalyticsService from "data/firestore/analytics";

import { getConfig, UserCardVariant } from "./UserCard.config";
import UserCardHeader from "./UserCardHeader";
import UserCardHeaderActions from "./UserCardHeaderActions";
import UserCardChips from "./UserCardChips";
import UserCardBio from "./UserCardBio";
import UserCardFooter from "./UserCardFooter";
import CategoryBreakdown from "./CategoryBreakdown";

const dividerColor = blueGrey[100];

const useStyles = makeStyles(() => ({
  // No vertical margin: inside a virtualized list a margin on the measured item
  // root collapses out of ResizeObserver's box, so Virtuoso mis-measures and the
  // scroll drifts. Spacing between cards is provided by the list item wrapper
  // instead. Border width is a constant 1px in both states (favorite only changes
  // the color) so toggling favorite never reflows the card height.
  card: {
    border: "1px solid",
    borderColor: "lightgrey",
  },
  cardFavorite: {
    border: "1px solid",
    borderColor: amber[700],
  },
  cardHeaderContent: {
    padding: 8,
    paddingBottom: 0,
  },
}));

// Vertical gap between cards, applied by the list wrapper (padding, not margin,
// so Virtuoso measures it) and reused by the profile page's standalone card.
export const CARD_GAP = 15;

export interface UserData {
  id?: string;
  uid?: string;
  name?: string;
  emoji?: string;
  bio?: string;
  username?: {
    display?: string;
    standardized?: string;
  };
  profileLink?: string;
  extraSupportRequested?: boolean;
  hasPublicImages?: boolean;
  levelStart?: string;
  levelCurrent?: string;
  intact?: boolean;
  lastTugged?: Date | string;
  timezone?: string;
  above18?: boolean;
  incognito?: boolean;
  messagesDisabled?: boolean;
  streak?: number;
  streakInDanger?: boolean;
  active?: boolean;
  location?: {
    city?: string;
    formatted?: string;
  } | null;
  dates?: {
    start?: Date | string;
    end?: Date | string;
  };
  stats?: {
    duration?: number;
    methods?: Record<string, number>;
  };
}

export interface UserCardProps {
  uid: string;
  user?: UserData;
  onKudosSent?: (sent: boolean) => void;
  onFavoriteChange?: () => void;
  customBio?: ReactNode | null;
  variant?: UserCardVariant;
  expanded?: boolean | null;
  showNavigateToProfile?: boolean;
  showKudos?: boolean;
  showDuration?: boolean;
  lastActivity?: Date | string | null;
}

// Once a user's data has loaded, keep it so a re-mounted card (e.g. a feed row
// scrolling back into view) shows the real name/avatar immediately instead of the
// generic placeholder while it re-loads.
const userCache = new Map<string, UserData>();

const UserCard = memo(function UserCard({
  uid,
  user: preloadedUser,
  onKudosSent,
  onFavoriteChange,
  customBio = null,
  variant: variantProp = "community",
  expanded: initialExpandedOverride = null,
  lastActivity = null,
  ...remainingProps
}: UserCardProps) {
  const config = getConfig(variantProp);
  const {
    showNavigateToProfile,
    showKudos,
    showDuration,
    expanded: initialExpandedConfig,
  } = { ...config, ...remainingProps };

  const navigate = useCustomNavigation();
  const openProfile = useOpenProfile();
  const { enqueueSnackbar } = useCustomSnackbar();
  const classes = useStyles();

  const [user, setUser] = useState<UserData | null>(
    preloadedUser || userCache.get(uid) || null
  );
  const [inProgress, setInProgress] = useState(false);
  // Plain per-instance state: it survives a profile push/pop (the list stays
  // mounted underneath) and resets when the page unmounts on a tab switch.
  const [expanded, setExpanded] = useState(() =>
    initialExpandedOverride !== null
      ? initialExpandedOverride
      : initialExpandedConfig
  );
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentUserAbove18, setCurrentUserAbove18] = useState(false);
  const [hiddenIncognito, setHiddenIncognito] = useState(false);
  const signedIn = CurrentSession.isSignedIn;

  // Derived user instance ensures we never show stale data during UID transitions.
  // We prioritize preloadedUser, then the local state ONLY if its ID matches the current UID.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const displayUser: any =
    preloadedUser || (user?.id === uid ? user : new User(uid as any));

  const isMyProfile =
    displayUser?.uid === CurrentSession.user?.id ||
    displayUser?.id === CurrentSession.user?.id;

  useEffect(() => {
    CurrentSession.load().then((currentUser) => {
      setCurrentUserAbove18(currentUser.above18 || false);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const viewedUser = preloadedUser || new User(uid as any);

    const finishLoading = () => {
      // Hide incognito users from community/feed views
      if (viewedUser.incognito && viewedUser.uid !== CurrentSession.user?.uid) {
        setHiddenIncognito(true);
        return;
      }
      userCache.set(uid, viewedUser as UserData); // real data for next re-mount
      setUser(viewedUser as UserData);
      // `active` is derived in Supabase from a live, recent in-progress session
      // (users_public, see migration 0029) -- it can't go stale and already
      // excludes orphaned never-stopped sessions, so trust it directly. No
      // per-card confirm query, no client-side recency gate.
      setInProgress(!!viewedUser.active);
      setIsFavorite(CurrentSession.user?.isFavorite?.(uid) || false);
    };

    if (preloadedUser) {
      finishLoading();
    } else {
      (viewedUser as User).load().then(finishLoading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, preloadedUser]);

  // Re-read the star when favorites change anywhere (e.g. toggled in the profile
  // overlay opened over this card). addFavorite/removeFavorite fire "userUpdated".
  useEffect(() => {
    const refresh = () =>
      setIsFavorite(CurrentSession.user?.isFavorite?.(uid) || false);
    window.addEventListener("userUpdated", refresh);
    return () => window.removeEventListener("userUpdated", refresh);
  }, [uid]);

  const handleProfileClick = () => {
    AnalyticsService.event("click_user", { user: uid });
    openProfile({ uid, username: displayUser.name });
  };

  const handleActionAreaClick = (e: MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleFavoriteToggle = async (e: MouseEvent) => {
    e.stopPropagation();
    if (isMyProfile) return;
    try {
      if (isFavorite) {
        await CurrentSession.user.removeFavorite(uid);
        setIsFavorite(false);
        enqueueSnackbar("Removed from favorites", { variant: "info" });
      } else {
        await CurrentSession.user.addFavorite(uid);
        setIsFavorite(true);
        enqueueSnackbar("Added to favorites", { variant: "success" });
      }
      if (onFavoriteChange) onFavoriteChange();
    } catch (error) {
      Logger.error("Failed to toggle favorite:", error);
      enqueueSnackbar("Failed to update favorites", { variant: "error" });
    }
  };

  if (!displayUser || hiddenIncognito) return null;

  return (
    <Card className={isFavorite ? classes.cardFavorite : classes.card}>
      <CardActionArea
        component="div"
        onClick={handleActionAreaClick}
        style={{
          padding: 5,
          pointerEvents: "initial",
          cursor: "pointer",
        }}
      >
        <UserCardHeader
          uid={uid}
          user={displayUser}
          classes={classes}
          onYourProfile={isMyProfile}
          inProgress={inProgress}
          signedIn={signedIn}
          action={
            <UserCardHeaderActions
              user={displayUser}
              isCurrentUser={isMyProfile}
            />
          }
        />
      </CardActionArea>

      <CardContent
        style={{
          padding: 0,
          display: "flex",
          flexFlow: "row",
          width: "100%",
          alignItems: "center",
        }}
      >
        <Grid container justifyContent="center">
          {/* Content: either customBio (feed post) OR bio (user profile) - never both */}
          {/* customBio is always visible; bio is inside collapse */}
          {customBio && (
            <Box sx={{ px: 2, pt: 1, pb: 0.5, width: "100%" }}>{customBio}</Box>
          )}

          {/* Expandable content: bio, then chips, then footer */}
          <Collapse
            in={expanded}
            timeout="auto"
            unmountOnExit
            style={{ width: "100%" }}
          >
            <Box sx={{ px: 2, pb: 1, width: "100%" }}>
              {/* Only show bio if no customBio was provided */}
              {!customBio && <UserCardBio user={displayUser} />}
              <UserCardChips
                user={displayUser}
                inProgress={inProgress}
                lastActivity={lastActivity}
                signedIn={signedIn}
              />
              {!isMyProfile && (
                <>
                  <Divider
                    style={{
                      width: "100%",
                      borderColor: dividerColor,
                      margin: "15px 0",
                    }}
                  />
                  <UserCardFooter
                    user={displayUser}
                    uid={uid}
                    isCurrentUser={isMyProfile}
                    isFavorite={isFavorite}
                    handleFavoriteToggle={handleFavoriteToggle}
                    currentUserAbove18={currentUserAbove18}
                    navigate={navigate}
                    handleProfileClick={handleProfileClick}
                    showNavigateToProfile={showNavigateToProfile}
                    showKudos={showKudos}
                    showDuration={showDuration}
                    onKudosSent={onKudosSent}
                  />
                </>
              )}
            </Box>
          </Collapse>
        </Grid>
      </CardContent>

      <CategoryBreakdown
        methodsData={_.get(displayUser, ["stats", "methods"], {})}
        uid={uid}
      />
    </Card>
  );
});

export default UserCard;
